import React, { useState, useEffect } from 'react';
import { 
  FolderIcon,
  FolderOpenIcon,
  DocumentTextIcon,
  UserGroupIcon,
  EnvelopeIcon,
  ReceiptPercentIcon,
  ShieldCheckIcon,
  ArchiveBoxIcon,
  DocumentIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import SearchableSelect from '../common/SearchableSelect';
import { useDocumentFolders } from '../../hooks/useDocumentFolders';
import { useCustomers } from '../../hooks/useCustomers';
import { FolderTreeNode } from '../../types/documentStorage';
import toast from 'react-hot-toast';

interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentFolderId?: string;
  parentFolderName?: string;
  folderToEdit?: FolderTreeNode | null;
}

const FOLDER_COLORS = [
  { name: 'Kék', value: '#3b82f6', bg: 'bg-blue-500' },
  { name: 'Zöld', value: '#16a34a', bg: 'bg-green-600' },
  { name: 'Lila', value: '#8b5cf6', bg: 'bg-violet-500' },
  { name: 'Narancs', value: '#f59e0b', bg: 'bg-amber-500' },
  { name: 'Piros', value: '#dc2626', bg: 'bg-red-600' },
  { name: 'Rózsaszín', value: '#ec4899', bg: 'bg-pink-500' },
  { name: 'Indigo', value: '#6366f1', bg: 'bg-indigo-500' },
  { name: 'Szürke', value: '#6b7280', bg: 'bg-gray-500' }
];

const FOLDER_ICONS = [
  { name: 'Mappa', value: 'FolderIcon', icon: FolderIcon },
  { name: 'Nyitott mappa', value: 'FolderOpenIcon', icon: FolderOpenIcon },
  { name: 'Dokumentum', value: 'DocumentTextIcon', icon: DocumentTextIcon },
  { name: 'Felhasználók', value: 'UserGroupIcon', icon: UserGroupIcon },
  { name: 'Email', value: 'EnvelopeIcon', icon: EnvelopeIcon },
  { name: 'Számla', value: 'ReceiptPercentIcon', icon: ReceiptPercentIcon },
  { name: 'Garancia', value: 'ShieldCheckIcon', icon: ShieldCheckIcon },
  { name: 'Archívum', value: 'ArchiveBoxIcon', icon: ArchiveBoxIcon }
];

export default function CreateFolderModal({ isOpen, onClose, parentFolderId, parentFolderName, folderToEdit }: CreateFolderModalProps) {
  const isEditMode = !!folderToEdit;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const [icon, setIcon] = useState('FolderIcon');
  const [loading, setLoading] = useState(false);

  const { createFolder, updateFolder } = useDocumentFolders();
  const { customers } = useCustomers();

  // Betöltjük a mappa adatait szerkesztési módban
  useEffect(() => {
    if (folderToEdit) {
      setName(folderToEdit.name || '');
      setDescription(folderToEdit.description || '');
      setCustomerId(folderToEdit.customerId || '');
      setColor(folderToEdit.color || '#3b82f6');
      setIcon(folderToEdit.icon || 'FolderIcon');
    } else {
      resetForm();
    }
  }, [folderToEdit]);

  const customerOptions = customers.map(customer => ({
    value: customer.id,
    label: customer.companyName || `${customer.firstName} ${customer.lastName}`,
    searchText: `${customer.companyName || ''} ${customer.firstName || ''} ${customer.lastName || ''} ${customer.email || ''}`.toLowerCase()
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('A mappa neve kötelező!');
      return;
    }

    setLoading(true);

    try {
      if (isEditMode && folderToEdit) {
        // Szerkesztési mód
        await updateFolder(folderToEdit.id, {
          name: name.trim(),
          description: description.trim(),
          customerId: customerId || undefined,
          color,
          icon
        });
        toast.success('Mappa sikeresen frissítve!');
      } else {
        // Létrehozási mód
        await createFolder({
          name: name.trim(),
          description: description.trim(),
          parentId: parentFolderId || undefined,
          customerId: customerId || undefined,
          color,
          icon
        });
        toast.success('Mappa sikeresen létrehozva!');
      }
      
      handleClose();
    } catch (error) {
      console.error('Error saving folder:', error);
      toast.error(isEditMode ? 'Hiba a mappa frissítése során' : 'Hiba a mappa létrehozása során');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setCustomerId('');
    setColor('#3b82f6');
    setIcon('FolderIcon');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={isEditMode ? "Mappa szerkesztése" : "Új mappa létrehozása"} size="4xl">
      <div className="p-6 space-y-6">
        {/* Szülő mappa jelzés */}
        {parentFolderId && parentFolderName && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex items-center text-sm">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
                <FolderIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <div className="text-gray-600 dark:text-gray-400">Almappa létrehozása itt:</div>
                <div className="font-semibold text-blue-700 dark:text-blue-300">{parentFolderName}</div>
              </div>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <FolderIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">Alapinformációk</h3>
                
                <div>
                  <label htmlFor="folderName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Mappa neve *
                  </label>
                  <input
                    type="text"
                    id="folderName"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="pl. Szerződések, Projektek, Archívum..."
                    required
                  />
                </div>

                <div>
                  <label htmlFor="folderDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Leírás
                  </label>
                  <textarea
                    id="folderDescription"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                    placeholder="Mappa leírása (opcionális)"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Customer Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-green-100 to-teal-100 dark:from-green-900/20 dark:to-teal-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <UserIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Ügyfél (opcionális)</h3>
                <SearchableSelect
                  options={customerOptions}
                  value={customerId}
                  onChange={(value) => setCustomerId(value || '')}
                  placeholder="Válasszon ügyfelet vagy hagyja üresen..."
                  searchPlaceholder="Ügyfél keresése..."
                  className="w-full"
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Ha kiválaszt egy ügyfelet, a mappa ahhoz lesz rendelve. Egyébként általános mappa lesz.
                </p>
              </div>
            </div>
          </div>

          {/* Appearance Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-purple-100 to-fuchsia-100 dark:from-purple-900/20 dark:to-fuchsia-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <ArchiveBoxIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">Megjelenés</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Szín
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {FOLDER_COLORS.map((colorOption) => (
                      <button
                        key={colorOption.value}
                        type="button"
                        onClick={() => setColor(colorOption.value)}
                        className={`
                          p-3 rounded-lg border-2 transition-all duration-200 hover:scale-105
                          ${color === colorOption.value
                            ? 'border-gray-400 dark:border-gray-500 ring-2 ring-primary-500 shadow-md'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }
                        `}
                      >
                        <div className={`w-6 h-6 rounded-full ${colorOption.bg} mx-auto shadow-sm`} />
                        <span className="text-xs text-gray-600 dark:text-gray-400 mt-1 block">
                          {colorOption.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ikon
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {FOLDER_ICONS.map((iconOption) => {
                      const IconComponent = iconOption.icon;
                      return (
                        <button
                          key={iconOption.value}
                          type="button"
                          onClick={() => setIcon(iconOption.value)}
                          className={`
                            p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center hover:scale-105
                            ${icon === iconOption.value
                              ? 'border-gray-400 dark:border-gray-500 ring-2 ring-primary-500 shadow-md'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                            }
                          `}
                        >
                          <IconComponent 
                            className="w-6 h-6 mb-1 transition-all duration-200"
                            style={{ color: color }}
                          />
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {iconOption.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-600">
                <DocumentIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">Előnézet</h3>
                <div className="flex items-center p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-xl border border-gray-200 dark:border-gray-600">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mr-4 shadow-sm border"
                    style={{ 
                      backgroundColor: color + '20',
                      borderColor: color + '40'
                    }}
                  >
                    {React.createElement(FOLDER_ICONS.find(i => i.value === icon)?.icon || FolderIcon, {
                      className: "w-6 h-6",
                      style: { color: color }
                    })}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 dark:text-gray-100 text-base">
                      {name || 'Mappa neve'}
                    </div>
                    {description && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {description}
                      </div>
                    )}
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mt-2">
                      <div className={`w-2 h-2 rounded-full mr-2`} style={{ backgroundColor: color }} />
                      {customerId ? (
                        <>
                          Ügyfél mappa • {customers.find(c => c.id === customerId)?.companyName || 
                          `${customers.find(c => c.id === customerId)?.firstName} ${customers.find(c => c.id === customerId)?.lastName}`}
                        </>
                      ) : (
                        'Általános mappa'
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
              className="px-6 py-2.5"
            >
              Mégse
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading || !name.trim()}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Létrehozás...
                </div>
              ) : (
                'Mappa létrehozása'
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}